/** @license DAFT Core v1.2.0
* Copyright 2020 Texas A&M Center for Applied Technology,
* Texas A&M Engineering Experiment Station, The Texas A&M
* University System.
*
* This source code/schema and any derivative work is Texas
* A&M Engineering Experiment Station intellectual property
* and protected by copyright law. Usage is restricted solely
* to the FARM Animal Care and Environmental Stewardship
* programs administered by the National Milk Producers
* Federation. Dissemination of this source code and/or
* derivative work creation is strictly prohibited without
* express written permission from the Texas A&M Engineering
* Experiment Station.
*/
import moment from 'moment';
import { ProcessorFunction } from './processor';
import { map } from 'rxjs/operators';
import { of } from 'rxjs';
import { DateFormatExpression, DateOffsetExpression, DateToMomentExpression } from './expressions';
import { DFSValueType } from './form';

export type TimeUnits = 'minute' | 'hour' | 'day' | 'month' | 'year';

var iso8601Local = 'YYYY-MM-DDTHH:mm:ss';
var iso8601TimeZone = 'YYYY-MM-DDTHH:mm:ssZ';
var iso8601Date = 'YYYY-MM-DD';

function isTimestampWithTimeZone(t: string) {
  return t.includes('Z') || t.includes('+') || t.length - t.lastIndexOf('-') === 6;
}

function isoStringToMoment(t: string) {
  if (t.includes('T')) {
    return isTimestampWithTimeZone(t) ? moment(t, iso8601TimeZone) : moment(t, iso8601Local);
  }
  return moment(t, iso8601Date);
}

export const $date: ProcessorFunction<DateToMomentExpression, moment.Moment> = function (t, e, o) {
  if (typeof t.date === 'string') {
    var r = t.date.toLowerCase();
    if (r === 'now') {
      return of(moment());
    }
    if (r === 'today') {
      return of(moment.parseZone(moment().format(iso8601Date)));
    }
  }
  return o.watch(t.date, e).pipe(
    map(function (e: DFSValueType) {
      if (!isStringOrMoment(e)) {
        return null;
      }

      if (t.format) {
        return t.format.includes('Z') ? moment.parseZone(e, t.format) : moment(e, t.format);
      }
      return isTimestampWithTimeZone(e) ? moment.parseZone(e, iso8601TimeZone) : moment(e, iso8601Local);
    })
  );
};

export const $dateOffset: ProcessorFunction<DateOffsetExpression, moment.Moment> = function (t, e, o) {
  return o.watch(t.date, e)
    .pipe(
      map(function (t: DFSValueType) {
        if (!isStringOrMoment(t)) {
          return null;
        }
        return typeof t === 'string' ? isoStringToMoment(t) : t;
      }),
      map(function (e: moment.Moment | null) {
        if (!moment.isMoment(e)) {
          return null;
        }

        var o = e!.clone();
        return o.add(t.offset, t.unit);
      })
    );
};

export const $dateFormat: ProcessorFunction<DateFormatExpression, string> = function (t, e, o) {
  return o.watch(t.date, e)
    .pipe(
      map(function (e: DFSValueType) {
        if (!isStringOrMoment(e)) {
          return null;
        }

        var o: moment.Moment = typeof e === 'string' ? isoStringToMoment(e) : e;

        return o.format(t.format);
      })
    );
};


function isStringOrMoment(r: DFSValueType): r is (string | moment.Moment) {
  return typeof r === 'string' || moment.isMoment(r);
}
